import { useMemo } from 'react'

import { queryOptions, useQuery } from '@tanstack/react-query'
import { orderBy, sortBy, uniqBy } from 'lodash'
import { erc4626Abi, getContract, zeroAddress } from 'viem'

import { SupportedChainId } from '~/constants/chains'
import { QUERY_KEYS } from '~/constants/react-query'
import { REFETCH_TOKENS } from '~/constants/refetch-intervals'
import { useActiveOrg } from '~/hooks/org'
import { useOrgBalances } from '~/hooks/useBalances'
import { useCardBalances } from '~/hooks/useCardBalances'
import { useCards } from '~/hooks/useCards'
import {
  useSmartAccounts,
  useSmartAccountsNFTBalances,
} from '~/hooks/useSmartAccounts'
import { useMultipleTokensMetadata } from '~/hooks/useTokenMetadata'
import { useGetGlobalSpamData } from '~/hooks/useVisibilityCheck'
import { IAddress } from '~/types'
import { TokenBalance } from '~/utils/account'
import { EvmAddress } from '~/utils/address'
import { getReadPublicClient } from '~/utils/requests'
import { getVaultApy } from '~/utils/vault'

export const useAllNFTTokens = () => {
  const { smartAccounts } = useSmartAccounts()
  const { balances, isLoading } = useSmartAccountsNFTBalances({
    smartAccounts,
    enabled: true,
    showSpamTokens: true,
  })
  const getGlobalSpamData = useGetGlobalSpamData()

  const nfts = useMemo(() => {
    const uniqNFTBalances = uniqBy(balances, (nft) =>
      [nft.chainId, nft.address, nft.tokenId].join('_'),
    )

    return orderBy(
      uniqNFTBalances,
      [
        (nft) => {
          const globalSpamData = getGlobalSpamData({ nft })
          if (globalSpamData?.isSpam) {
            const spamData = globalSpamData.spamData
            // Spam NFTs without score should be sorted after not-spam nfts
            // and before any other spam ones, so this is a way to do it.
            return spamData.score ?? 0.00001
          }
          return 0
        },
      ],
      ['asc'],
    )
  }, [balances, getGlobalSpamData])

  return { nfts, isLoading }
}

// Cards are watched addresses, not smart accounts, so their tokens aren't in
// org balances; include them (spam included) so hidden card tokens can be unhidden
const useCardTokenBalances = () => {
  const { cards } = useCards()
  // Orgs are limited to one card for now
  const card = cards[0]
  const { balances, isLoading } = useCardBalances(card?.address, {
    showSpamTokens: true,
  })

  const cardTokenBalances = useMemo<TokenBalance[]>(() => {
    if (!card) return []
    return balances.map((balance) => ({
      ownerAddress: card.address,
      amount: BigInt(balance.amount),
      chainId: balance.chainId,
      address: balance.address,
      usdValue: balance.usdValue ?? 0,
      price: undefined,
      // The settings list reads visibility entries for the toggle, not this.
      whitelisted: false,
      tokenMetadata: {
        symbol: balance.symbol ?? '',
        decimals: balance.decimals,
        chainId: balance.chainId,
        address: balance.address,
      },
    }))
  }, [card, balances])

  return { cardTokenBalances, isLoading }
}

// NOTE: Doesn't include native token (ETH)
export const useAllERC20Tokens = () => {
  const { org } = useActiveOrg()

  const { balances, isLoading } = useOrgBalances(org?.id, true)
  const { cardTokenBalances, isLoading: isLoadingCardBalances } =
    useCardTokenBalances()

  // useMultipleTokensMetadata keys its metadata and logo queries on the list it
  // receives. Prices move between refetches, so the USD order below would mint
  // a new query (and a full logos request) per reorder; this order does not move.
  const tokenBalances = useMemo(() => {
    const allBalances = Object.values(balances)
      .flatMap((account) => account.balances)
      .concat(cardTokenBalances)
    const uniqTokenBalances = uniqBy(allBalances, (balance) =>
      [balance.chainId, balance.address].join('_'),
    )
    const erc20TokenBalances = uniqTokenBalances.filter(
      (balance) => balance.address !== zeroAddress,
    )
    return sortBy(erc20TokenBalances, ['chainId', 'address'])
  }, [balances, cardTokenBalances])

  const metadata = useMultipleTokensMetadata(tokenBalances)

  const tokens = orderBy(
    tokenBalances.map((balance, index) => {
      return { ...balance, metadata: metadata.at(index)! }
    }),
    [(token) => token.usdValue],
    ['desc'],
  )

  return { tokens, isLoading: isLoading || isLoadingCardBalances }
}

// The underlying assets `balance` vault shares redeem for, read on-chain. Shared
// so useQueries callers dedupe with useVaultAssets.
export const getVaultAssetsQuery = ({
  vault,
  chainId,
  balance,
}: {
  vault: IAddress
  chainId: SupportedChainId
  balance: bigint
}) =>
  queryOptions({
    queryKey: [QUERY_KEYS.VAULT_ASSETS, chainId, vault, balance.toString()],
    queryFn: () => {
      const contract = getContract({
        client: getReadPublicClient(chainId),
        address: EvmAddress.format(vault),
        abi: erc4626Abi,
      })
      return contract.read.previewRedeem([balance])
    },
    staleTime: REFETCH_TOKENS,
    refetchInterval: REFETCH_TOKENS,
  })

export const useVaultAssets = ({
  vault,
  chainId,
  balance,
  enabled = true,
}: {
  vault: IAddress
  chainId: SupportedChainId
  balance: bigint
  enabled?: boolean
}) => {
  return useQuery({
    ...getVaultAssetsQuery({ vault, chainId, balance }),
    enabled,
  })
}

export type ERC20TokenWithMetadata = ReturnType<
  typeof useAllERC20Tokens
>['tokens'][number]

export const useVaultApy = (
  chainId: SupportedChainId,
  vault: IAddress | undefined,
  { enabled = true }: { enabled?: boolean } = {},
) => {
  return useQuery({
    queryKey: [QUERY_KEYS.VAULT_APY, chainId, vault],
    queryFn: () => getVaultApy(chainId, EvmAddress.format(vault!)),
    staleTime: REFETCH_TOKENS,
    refetchInterval: REFETCH_TOKENS,
    enabled: enabled && !!vault,
  })
}
